import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';

const MONTHS = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);
const INPUT_CLS = "w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none";
const SELECT_CLS = `${INPUT_CLS} bg-white`;

function diffColor(diff) {
  if (diff > 0) return 'text-[#00C853]';
  if (diff < 0) return 'text-[#DC2626]';
  return 'text-[#6B7280]';
}

function saveButtonLabel(saving, editId) {
  if (saving) return 'A guardar...';
  return editId ? 'Guardar' : 'Criar';
}

function ForecastForm({ form, setForm, editId, saving, result, onSave, onCancel }) {
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm" data-testid="forecast-form">
      <h3 className="text-sm font-semibold text-[#111827] mb-4">{editId ? 'Editar Previsão' : 'Nova Previsão'}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Ano</label>
          <input data-testid="fc-ano" type="number" value={form.ano} onChange={e => set('ano', e.target.value)} disabled={!!editId}
            className={`${INPUT_CLS} ${editId ? 'bg-[#F9FAFB]' : ''}`} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Mês</label>
          <select data-testid="fc-mes" value={form.mes} onChange={e => set('mes', e.target.value)} disabled={!!editId}
            className={`${SELECT_CLS} ${editId ? 'bg-[#F9FAFB]' : ''}`}>
            {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Vol. Pessoal</label>
          <input data-testid="fc-pessoal" type="number" step="0.01" value={form.volume_pessoal} onChange={e => set('volume_pessoal', e.target.value)}
            placeholder="0.00" className={INPUT_CLS} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Vol. Kits</label>
          <input data-testid="fc-kits" type="number" step="0.01" value={form.volume_kits} onChange={e => set('volume_kits', e.target.value)}
            placeholder="0.00" className={INPUT_CLS} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Vol. Clientes</label>
          <input data-testid="fc-clientes" type="number" step="0.01" value={form.volume_clientes} onChange={e => set('volume_clientes', e.target.value)}
            placeholder="0.00" className={INPUT_CLS} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#6B7280] mb-1">Nº Diretos Novos</label>
          <input data-testid="fc-diretos" type="number" min="0" step="1" value={form.num_diretos_novos ?? ''}
            onChange={e => set('num_diretos_novos', e.target.value)}
            placeholder="0" className={INPUT_CLS} />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={onSave} disabled={saving} data-testid="fc-save-btn"
          className="px-5 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#001f6e] disabled:opacity-40 transition-colors">
          {saveButtonLabel(saving, editId)}
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-sm text-[#6B7280] hover:text-[#111827]">Cancelar</button>
        {result && !result.success && <span className="text-sm text-red-600">{result.message}</span>}
      </div>
    </div>
  );
}

/**
 * "Previsões Pessoais" tab — form + table of personal monthly forecasts.
 * Real column comes from actual invoicing when viewing the current year.
 */
export default function PersonalTab({
  forecasts, realByMonth, diretosRealByMonth,
  showForm, form, setForm, editId, saving, result,
  onNew, onEdit, onSave, onCancel, onDelete,
}) {
  const currentYear = new Date().getFullYear();
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-semibold text-[#111827]">Previsões Pessoais</h2>
        <button onClick={onNew} data-testid="new-forecast-btn"
          className="flex items-center gap-2 px-4 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#001f6e] transition-colors">
          <Plus size={16} /> Nova Previsão
        </button>
      </div>

      {showForm && (
        <ForecastForm form={form} setForm={setForm} editId={editId} saving={saving} result={result} onSave={onSave} onCancel={onCancel} />
      )}

      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="forecasts-table">
            <thead>
              <tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                <th className="px-4 py-2.5 text-left font-medium">Período</th>
                <th className="px-3 py-2.5 text-right font-medium">Pessoal (P)</th>
                <th className="px-3 py-2.5 text-right font-medium">Kits (P)</th>
                <th className="px-3 py-2.5 text-right font-medium">Clientes (P)</th>
                <th className="px-3 py-2.5 text-right font-semibold text-[#0F1829]">Total (P)</th>
                <th className="px-3 py-2.5 text-right font-semibold text-[#00C853]">Total (R)</th>
                <th className="px-3 py-2.5 text-right font-semibold text-[#111827]">Diferença</th>
                <th className="px-3 py-2.5 text-right font-semibold text-[#0F1829]" title="Diretos: Objetivo / Real (variação %)">Diretos (P/R · %)</th>
                <th className="px-3 py-2.5 text-center font-medium w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {forecasts.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#9CA3AF]">Sem previsões. Crie a primeira.</td></tr>}
              {forecasts.map(f => {
                const totalP = (f.VolumePessoal_P || 0) + (f.VolumeKits_P || 0) + (f.VolumeClientes_P || 0);
                const storedR = (f.VolumePessoal_R || 0) + (f.VolumeKits_R || 0) + (f.VolumeClientes_R || 0);
                const totalR = f.Ano === currentYear && realByMonth[f.Mes] != null ? Number(realByMonth[f.Mes] || 0) : storedR;
                const diff = totalR - totalP;
                const diffPct = totalP > 0 ? (diff / totalP) * 100 : null;
                const diretosP = Number(f.NumDiretosNovos_P || 0);
                const diretosKey = `${f.Ano}-${f.Mes}`;
                const diretosR = (diretosRealByMonth && diretosRealByMonth[diretosKey] != null)
                  ? Number(diretosRealByMonth[diretosKey])
                  : Number(f.NumDiretosNovos_R || 0);
                const diretosDiff = diretosR - diretosP;
                const diretosPct = diretosP > 0
                  ? (diretosDiff / diretosP) * 100
                  : (diretosR > 0 ? 100 : null);
                return (
                  <tr key={f.ID_Forecast} className="hover:bg-[#F9FAFB]" data-testid={`forecast-${f.ID_Forecast}`}>
                    <td className="px-4 py-2.5 font-medium text-[#111827]">{MONTHS[f.Mes]} {f.Ano}</td>
                    <td className="px-3 py-2.5 text-right text-[#374151]">{fmt(f.VolumePessoal_P)}</td>
                    <td className="px-3 py-2.5 text-right text-[#374151]">{fmt(f.VolumeKits_P)}</td>
                    <td className="px-3 py-2.5 text-right text-[#374151]">{fmt(f.VolumeClientes_P)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[#0F1829]">{fmt(totalP)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[#00C853]">{fmt(totalR)}</td>
                    <td className={`px-3 py-2.5 text-right font-semibold tabular-nums ${diffColor(diff)}`} data-testid={`diff-${f.ID_Forecast}`}>
                      {diff > 0 ? '+' : ''}{fmt(diff)}
                      {diffPct != null && (
                        <span className="ml-1 text-xs font-normal opacity-80">
                          ({diffPct > 0 ? '+' : ''}{diffPct.toFixed(1)}%)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums" data-testid={`diretos-cell-${f.ID_Forecast}`}>
                      {diretosP === 0 && diretosR === 0
                        ? <span className="text-[#9CA3AF]">—</span>
                        : <>
                            <span className="text-[#6B7280]">{diretosP}</span>
                            <span className="text-[#9CA3AF] mx-1">/</span>
                            <span className={`font-semibold ${diffColor(diretosDiff)}`}>{diretosR}</span>
                            {diretosPct != null && (
                              <span className={`ml-1 text-xs font-normal ${diffColor(diretosDiff)}`}>
                                ({diretosPct > 0 ? '+' : ''}{diretosPct.toFixed(1)}%)
                              </span>
                            )}
                          </>
                      }
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex items-center gap-1 justify-center">
                        {f.Alteravel === 'S' && (
                          <button onClick={() => onEdit(f)} className="p-1 rounded hover:bg-blue-50 text-[#9CA3AF] hover:text-[#0F1829]"><PencilSimple size={16} /></button>
                        )}
                        <button onClick={() => onDelete(f.ID_Forecast)} className="p-1 rounded hover:bg-red-50 text-[#9CA3AF] hover:text-red-500"><Trash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
